/** Searchable display units, with an explicit Custom choice for free text. */
import { html, nothing, type PropertyValues, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../lit-base.ts';
import { isPresetUnit, UNIT_OPTIONS } from '../utils/units.ts';
import './plain-select.ts';

const CUSTOM = '__custom__';
const OPTIONS = [...UNIT_OPTIONS, { value: CUSTOM, label: 'Custom…', alwaysVisible: true }];
let nextId = 0;

@customElement('unit-picker')
export class UnitPicker extends LightElement {
  @property() value = '';
  @property({ attribute: false }) select: (value: string) => void = () => {};
  @state() private customMode = false;
  private localValue: string | undefined;
  private customId = `custom-unit-${++nextId}`;

  override willUpdate(changed: PropertyValues<this>): void {
    if (changed.has('value')) {
      if (this.value !== this.localValue) this.customMode = !isPresetUnit(this.value);
      this.localValue = undefined;
    }
  }

  private commit(value: string): void {
    this.localValue = value.slice(0, 40);
    this.value = this.localValue;
    this.select(this.value);
  }

  private pick(value: string): void {
    this.customMode = value === CUSTOM;
    if (this.customMode) {
      void this.updateComplete.then(() => this.querySelector<HTMLInputElement>('.unit-picker-custom input')?.focus());
    } else this.commit(value);
  }

  override render(): TemplateResult {
    return html`
      <plain-select label="Unit" .searchable=${true} searchPlaceholder="Search units…"
        .options=${OPTIONS} .value=${this.customMode ? CUSTOM : this.value}
        .select=${(value: string) => this.pick(value)}></plain-select>
      ${this.customMode ? html`<div class="axis-block unit-picker-custom">
        <label for=${this.customId}>Custom unit</label>
        <input type="text" id=${this.customId} maxlength="40" placeholder="Enter unit…"
          .value=${this.value} @input=${(e: Event) => this.commit((e.target as HTMLInputElement).value)} />
      </div>` : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'unit-picker': UnitPicker; }
}
